// ════════════════════════════════════════════════════════════════════
// OurStory — marketing "our story" page, in the same slyce-style layout and
// Netrev palette as the landing page (shared nav, footer, base CSS and
// motion from SiteChrome.js). Each chapter gets its own illustration
// (LandingArt.js). Copy lives in lib/i18n-catalog/story.js.
// ════════════════════════════════════════════════════════════════════
import { useRef, useState } from "react";
import { useT } from "../lib/i18n";
import { LANDING_ANCHOR_KEY, SITE_CSS, SiteNav, SiteFooter, BackToTop, tidy, useSiteMotion, useScrollState, useParallax } from "./SiteChrome";
import { StoryHeroArt, ChapterArt } from "./LandingArt";

const STORY_CSS = `
  .st-back { display:inline-flex; align-items:center; gap:7px; padding:0; margin:0 0 20px; border:none; background:none; color:var(--muted); font-size:14px; font-weight:600; cursor:pointer; transition:color .15s; }
  .st-back:hover { color:var(--blue); }
  html[dir="rtl"] .st-back svg { transform:scaleX(-1); }
  .st-sec { padding-top:64px; padding-bottom:64px; }
  .st-num { display:inline-flex; align-items:center; justify-content:center; width:52px; height:52px; border-radius:16px; color:#fff; font-family:var(--head); font-size:18px; font-weight:800;
    background:linear-gradient(135deg, var(--navy), var(--royal)); box-shadow:0 12px 24px -12px rgba(20,34,110,.7); margin-bottom:18px; }
  .st-body { font-size:17px; line-height:1.75; color:var(--body); margin:0; max-width:580px; text-wrap:pretty; }
  .st-cta { border-radius:28px; padding:64px 48px; text-align:center; color:#fff; position:relative; overflow:hidden;
    background:linear-gradient(135deg, var(--navy) 0%, var(--royal) 100%); box-shadow:0 40px 80px -40px rgba(20,34,110,.7); }
  .st-cta::before, .st-cta::after { content:""; position:absolute; border-radius:50%; border:1px solid rgba(255,255,255,.16); pointer-events:none; }
  .st-cta::before { width:520px; height:520px; top:-260px; inset-inline-end:-160px; box-shadow:0 0 0 60px rgba(255,255,255,.03), 0 0 0 120px rgba(255,255,255,.02); }
  .st-cta::after { width:360px; height:360px; bottom:-200px; inset-inline-start:-120px; }
  .st-cta h2 { color:#fff; position:relative; }
  .st-cta p { position:relative; font-size:17px; line-height:1.6; color:rgba(255,255,255,.84); margin:0 auto 30px; max-width:560px; }
  .st-cta .lp-ctas { justify-content:center; position:relative; }
  @media (max-width: 900px) {
    .st-sec { padding-top:48px; padding-bottom:48px; }
    .st-body { font-size:16px; }
    .st-cta { padding:44px 22px; border-radius:22px; }
  }
`;

export function OurStory({ onBack, onStart, onLegal }) {
  const { lang, t } = useT();
  const rtl = lang === "ar";
  const rootRef = useRef(null);
  const heroArtRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrolled, deep } = useScrollState();

  useSiteMotion(rootRef, lang);
  useParallax(heroArtRef);

  // Landing sections live on the home page: remember where to land, then go home.
  function goHome(anchor) {
    setMenuOpen(false);
    try {
      if (anchor) window.sessionStorage.setItem(LANDING_ANCHOR_KEY, anchor);
    } catch (_) { /* storage blocked: land at the top */ }
    onBack();
  }

  const navLinks = [
    { key: "product", l: t("landing.nav.product"), fn: () => goHome("product") },
    { key: "partners", l: t("landing.nav.partners"), fn: () => goHome("partners") },
    { key: "pricing", l: t("landing.nav.pricing"), fn: () => goHome("pricing") },
    { key: "story", l: t("landing.nav.ourStory"), current: true, fn: () => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); } },
    { key: "contact", l: t("landing.nav.contact"), fn: () => goHome("contact") },
  ];

  const footerCols = [
    {
      key: "product", title: t("landing.footer.col.product"),
      links: [
        { key: "insight", l: t("landing.p1.eyebrow"), fn: () => goHome("product") },
        { key: "ai", l: t("landing.p2.eyebrow"), fn: () => goHome("ai") },
        { key: "price-tool", l: t("landing.p3.eyebrow"), fn: () => goHome("price-tool") },
        { key: "pricing", l: t("landing.footer.link.pricing"), fn: () => goHome("pricing") },
        { key: "signin", l: t("landing.footer.link.signIn"), fn: () => onStart("login") },
      ],
    },
    {
      key: "company", title: t("landing.footer.col.company"),
      links: [
        { key: "story", l: t("landing.nav.ourStory"), fn: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
        { key: "partners", l: t("landing.nav.partners"), fn: () => goHome("partners") },
        { key: "contact", l: t("landing.footer.link.contact"), fn: () => goHome("contact") },
        { key: "demo", l: t("landing.footer.link.bookDemo"), fn: () => goHome("book") },
      ],
    },
    {
      key: "legal", title: t("landing.footer.col.legal"),
      links: [
        { key: "privacy", l: t("landing.footer.legal.privacy"), fn: () => onLegal && onLegal("privacy") },
        { key: "terms", l: t("landing.footer.legal.terms"), fn: () => onLegal && onLegal("terms") },
        { key: "cookies", l: t("landing.footer.legal.cookies"), fn: () => onLegal && onLegal("privacy") },
      ],
    },
  ];

  const chapters = [
    { n: "01", kind: "busy", tag: t("story.ch1.tag"), body: t("story.ch1.body") },
    { n: "02", kind: "kitchens", tag: t("story.ch2.tag"), body: t("story.ch2.body") },
    { n: "03", kind: "clicked", tag: t("story.ch3.tag"), body: t("story.ch3.body") },
    { n: "04", kind: "sharing", tag: t("story.ch4.tag"), body: t("story.ch4.body") },
  ];
  const artText = {
    orders: t("story.art.orders"), profit: t("story.art.profit"), breakeven: t("story.art.breakeven"),
    itemA: t("story.art.itemA"), itemB: t("story.art.itemB"), margin: t("story.art.margin"),
  };
  const gap = { eyebrow: t("landing.art.gap.eyebrow"), pos: t("landing.art.gap.pos"), bank: t("landing.art.gap.bank"), deducted: t("landing.art.gap.deducted") };

  return (
    <div ref={rootRef} className={"lp" + (rtl ? " lp-rtl" : "")}>
      <style>{SITE_CSS + STORY_CSS}</style>

      <SiteNav
        t={t} rtl={rtl} links={navLinks} scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen}
        onHome={() => goHome(null)} onSignIn={() => onStart("login")} onStartFree={() => onStart("signup")} onBook={() => goHome("book")}
      />

      {/* HERO */}
      <section className="lp-hero lp-tint">
        <div className="lp-wrap lp-hero-grid">
          <div className="lp-hero-copy">
            <button type="button" className="st-back" data-reveal="" onClick={() => goHome(null)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              {t("story.backToHome")}
            </button>
            <h1 className="lp-h1" data-reveal="" style={{ "--d": 2 }}>{tidy(t("story.hero.heading"), rtl)}</h1>
            <p className="lp-lead" data-reveal="" style={{ "--d": 3 }}>{t("story.hero.sub")}</p>
            <div className="lp-ctas" data-reveal="" style={{ "--d": 4 }}>
              <button type="button" className="lp-btn lp-btn-primary" onClick={() => onStart("signup")}>{t("landing.hero.ctaTry")}</button>
              <button type="button" className="lp-btn lp-btn-ghost" onClick={() => goHome("book")}>{t("landing.hero.ctaDemo")}</button>
            </div>
          </div>
          <div className="lp-hero-art" data-reveal="" style={{ "--d": 1 }} ref={heroArtRef}>
            <StoryHeroArt label={t("landing.hero.artAlt")} gap={gap} rtl={rtl} />
          </div>
        </div>
      </section>

      {/* CHAPTERS — alternating text and illustration */}
      {chapters.map((c, i) => (
        <section key={c.n} className="lp-sec st-sec">
          <div className="lp-wrap">
            <div className={"lp-split" + (i % 2 ? " lp-flip" : "")}>
              <div className="lp-copy">
                <div className="st-num" data-reveal="pop">{c.n}</div>
                <h2 className="lp-h2" data-reveal="" style={{ "--d": 1 }}>{tidy(c.tag, rtl)}</h2>
                <p className="st-body" data-reveal="" style={{ "--d": 2 }}>{c.body}</p>
              </div>
              <div className="lp-art" data-reveal={i % 2 ? "left" : "right"} style={{ "--d": 1 }}>
                <ChapterArt kind={c.kind} label={c.tag} text={artText} rtl={rtl} />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CLOSING CTA */}
      <section className="lp-sec">
        <div className="lp-wrap">
          <div className="st-cta" data-reveal="">
            <h2 className="lp-h2">{tidy(t("story.cta.heading"), rtl)}</h2>
            <p>{t("story.cta.sub")}</p>
            <div className="lp-ctas">
              <button type="button" className="lp-btn lp-btn-white" onClick={() => onStart("signup")}>{t("story.cta.button")}</button>
              <button type="button" className="lp-btn lp-btn-outline-white" onClick={() => goHome("book")}>{t("landing.hero.ctaDemo")}</button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter t={t} cols={footerCols} />
      <BackToTop show={deep} label={t("landing.backToTop")} />
    </div>
  );
}
